import React, { useCallback, useEffect } from 'react';
import { Modal, useResponsive } from '@sweetgreen/garnish';

import type { DeliveryPreferenceType } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { OverlayToggleState, useOverlaysMachine } from '../Overlays';
import { DeliveryPreferencesForm } from './DeliveryPreferencesForm';
import { DeliveryPreferencesItem } from './DeliveryPreferencesItem';

const DELIVERY_PREFERENCES_OVERLAY_ID = 'delivery_preferences';

export const DeliveryPreferencesSelector = (
  props: DeliveryPreferencesSelectorProps,
) => {
  const { preferences, submitting, onSave } = props;

  const { match } = useResponsive();
  const { t } = useLocalizationContext();
  const { overlayId, sendOverlayEvent } = useOverlaysMachine();

  const openModal = useCallback(() => {
    sendOverlayEvent({
      overlayId: DELIVERY_PREFERENCES_OVERLAY_ID,
      toggleState: OverlayToggleState.OPEN,
    });
  }, [sendOverlayEvent]);

  const closeModal = useCallback(() => {
    sendOverlayEvent({
      overlayId: DELIVERY_PREFERENCES_OVERLAY_ID,
      toggleState: OverlayToggleState.CLOSE,
    });
  }, [sendOverlayEvent]);

  const saveForm = useCallback(
    (payload: DeliveryPreferences) => {
      onSave(payload);
    },
    [onSave],
  );

  useEffect(() => {
    if (submitting) return;
    closeModal();
  }, [submitting, closeModal]);

  return (
    <>
      <DeliveryPreferencesItem preferences={preferences} onPress={openModal} />

      <Modal
        animationType="fade"
        size={match(['full', 'small'])}
        accessibilityLabel={t('addresses.preferences.a11y')}
        visible={overlayId === DELIVERY_PREFERENCES_OVERLAY_ID}
        onRequestClose={closeModal}
      >
        <DeliveryPreferencesForm
          preferences={preferences}
          submitting={submitting}
          onSave={saveForm}
          onClose={closeModal}
        />
      </Modal>
    </>
  );
};

export type DeliveryPreferences = {
  readonly type: DeliveryPreferenceType;
  readonly notes?: string;
};

type DeliveryPreferencesSelectorProps = {
  readonly preferences: DeliveryPreferences;
  readonly submitting?: boolean;
  readonly onSave: (preferences: DeliveryPreferences) => void;
};
